// The pen grid: a raster of 0.25-unit cells over one pool. It decides where
// the dark undertow can reach and which water the wakes have cut off (lit).
import { ONE } from './num.js';

export const CELL = ONE / 4;
export const OUT = 0, OPEN = 1, SOLID = 2, LIT = 3;
const N4 = [[1, 0], [-1, 0], [0, 1], [0, -1]];

// Even-odd point in polygon (integer cross products, no division).
export function insidePoly(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y)) {
      // x < xi + (y - yi) * (xj - xi) / (yj - yi), sign-safe without division
      const lhs = (x - xi) * (yj - yi), rhs = (y - yi) * (xj - xi);
      if (yj > yi ? lhs < rhs : lhs > rhs) inside = !inside;
    }
  }
  return inside;
}

// Placement of the cell raster over a polygon (shared with the glass renderer).
export function gridFrame(poly) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of poly) { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); }
  // One spare cell of OUT around the polygon so neighbours never leave the array.
  const x0 = Math.floor(minX / CELL) * CELL - CELL, y0 = Math.floor(minY / CELL) * CELL - CELL;
  return { x0, y0, w: Math.ceil((maxX - x0) / CELL) + 2, h: Math.ceil((maxY - y0) / CELL) + 2 };
}

export function makeGrid(poly) {
  const { x0, y0, w, h } = gridFrame(poly);
  const g = {
    x0, y0, w, h,
    kind: new Uint8Array(w * h),
    dark: new Uint8Array(w * h),
    wake: new Int16Array(w * h),    // turn+1 of the last write; 0 = never
    owner: new Uint8Array(w * h),   // bit per crab that painted the cell (this stamp)
    tick: new Int32Array(w * h),    // tick of the last write in that turn
    turnOf: new Int16Array(w * h),  // turn stamp (S) of the last write; wake[] is when it stops blocking
    logMark: new Uint8Array(w * h), // 1/2 = covered by driftwood (was OPEN / was LIT)
    pen: new Uint8Array(w * h),     // crab bitmask of the pen that lit the cell (16 = the calmed nushi)
    penBy: new Uint8Array(w * h),   // 1 + the crab whose wake closed that pen (0 = none)
  };
  for (let cy = 0; cy < h; cy++) for (let cx = 0; cx < w; cx++) {
    const [px, py] = centreOf(g, cx + cy * w);
    if (insidePoly(poly, px, py)) g.kind[cx + cy * w] = OPEN;
  }
  return g;
}

export const centreOf = (g, i) => [g.x0 + (i % g.w) * CELL + CELL / 2, g.y0 + Math.floor(i / g.w) * CELL + CELL / 2];

export function cellAt(g, x, y) {
  const cx = Math.floor((x - g.x0) / CELL), cy = Math.floor((y - g.y0) / CELL);
  if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return -1;
  return cx + cy * g.w;
}

// Visit cells whose centre lies within r of (x, y) without allocating.
export function forDisc(g, x, y, r, fn) {
  const c0 = Math.floor((x - r - g.x0) / CELL), c1 = Math.floor((x + r - g.x0) / CELL);
  const r0 = Math.floor((y - r - g.y0) / CELL), r1 = Math.floor((y + r - g.y0) / CELL);
  for (let cy = Math.max(0, r0); cy <= Math.min(g.h - 1, r1); cy++) {
    for (let cx = Math.max(0, c0); cx <= Math.min(g.w - 1, c1); cx++) {
      const px = g.x0 + cx * CELL + CELL / 2 - x, py = g.y0 + cy * CELL + CELL / 2 - y;
      if (px * px + py * py <= r * r) fn(cx + cy * g.w);
    }
  }
}

// Cells whose centre lies within r of (x, y).
export function cellsInDisc(g, x, y, r) {
  const out = [];
  const c0 = Math.floor((x - r - g.x0) / CELL), c1 = Math.floor((x + r - g.x0) / CELL);
  const r0 = Math.floor((y - r - g.y0) / CELL), r1 = Math.floor((y + r - g.y0) / CELL);
  for (let cy = Math.max(0, r0); cy <= Math.min(g.h - 1, r1); cy++) {
    for (let cx = Math.max(0, c0); cx <= Math.min(g.w - 1, c1); cx++) {
      const px = g.x0 + cx * CELL + CELL / 2 - x, py = g.y0 + cy * CELL + CELL / 2 - y;
      if (px * px + py * py <= r * r) out.push(cx + cy * g.w);
    }
  }
  return out;
}

export function markSolid(g, x, y, r) {
  for (const i of cellsInDisc(g, x, y, r)) if (g.kind[i] === OPEN) g.kind[i] = SOLID;
}

export const wakeActive = (g, i, turn) => g.wake[i] >= turn && g.wake[i] > 0;

// Water the undertow can move through right now.
export const passable = (g, i, turn) => g.kind[i] === OPEN && !wakeActive(g, i, turn);

function neighbours(g, i, fn) {
  const cx = i % g.w, cy = (i - cx) / g.w;
  for (const [dx, dy] of N4) {
    const nx = cx + dx, ny = cy + dy;
    if (nx >= 0 && ny >= 0 && nx < g.w && ny < g.h) fn(nx + ny * g.w);
  }
}

// Breadth-first search from `seeds` through passable cells. Returns the visit
// order and a per-cell distance (-1 = unreached). Both are shared scratch
// buffers, valid until the next flood() call (this runs thousands of times a
// turn inside the planner, so it must not allocate).
let scratchDist = new Int16Array(0), scratchOrder = new Int32Array(0);
export function flood(g, seeds, turn, limit = Infinity) {
  const n = g.w * g.h;
  if (scratchDist.length < n) { scratchDist = new Int16Array(n); scratchOrder = new Int32Array(n); }
  const dist = scratchDist, q = scratchOrder;
  dist.fill(-1, 0, n);
  let len = 0;
  for (const s of seeds) if (dist[s] < 0) { dist[s] = 0; q[len++] = s; }
  const w = g.w, kind = g.kind, wake = g.wake;
  for (let k = 0; k < len; k++) {
    const i = q[k], d = dist[i];
    if (d >= limit) continue;
    const cx = i % w;
    // Inline 4-neighbourhood; the spare OUT border keeps indices in range.
    for (const nb of [i + 1, i - 1, i + w, i - w]) {
      if (nb < 0 || nb >= n || dist[nb] >= 0) continue;
      if ((nb === i + 1 && cx === w - 1) || (nb === i - 1 && cx === 0)) continue;
      if (kind[nb] !== OPEN || (wake[nb] > 0 && wake[nb] >= turn)) continue;
      dist[nb] = d + 1; q[len++] = nb;
    }
  }
  return { order: q.subarray(0, len), dist };
}

export function darkCells(g) {
  const out = [];
  for (let i = 0; i < g.dark.length; i++) if (g.dark[i]) out.push(i);
  return out;
}

// Light every open cell the dark can no longer reach. Wake cells that touch
// no reachable water light too, so a closed pen includes its own border.
// Returns the newly lit cells (ordered from the border inward) and the crab
// bitmask of the wakes that formed the border.
export function enclose(g, turn) {
  const { dist } = flood(g, darkCells(g), turn);
  // `dist` is scratch: keep only what we need before anything else floods.
  const lit = [];
  const mark = new Uint8Array(g.w * g.h);
  for (let i = 0; i < g.kind.length; i++) {
    if (g.kind[i] === OPEN && dist[i] < 0 && !wakeActive(g, i, turn)) { lit.push(i); mark[i] = 1; }
  }
  if (!lit.length) return { lit, crabs: 0 };
  // Light seeps from the pen into its wake border, but never into wake cells
  // that touch water the dark can still reach.
  let crabs = 0;
  for (let k = 0; k < lit.length; k++) {
    neighbours(g, lit[k], n => {
      if (mark[n] || g.kind[n] !== OPEN || !wakeActive(g, n, turn)) return;
      crabs |= g.owner[n];
      let touchesDark = false;
      neighbours(g, n, m => { if (dist[m] >= 0) touchesDark = true; });
      if (touchesDark) return;
      mark[n] = 1; lit.push(n);
    });
  }
  for (const i of lit) { g.kind[i] = LIT; g.dark[i] = 0; }
  return { lit: borderFirst(g, lit), crabs };
}

// Order cells by distance from the region's edge so the renderer can let the
// light flood in from the wakes.
function borderFirst(g, cells) {
  const inSet = new Uint8Array(g.w * g.h);
  for (const i of cells) inSet[i] = 1;
  const seen = new Uint8Array(g.w * g.h);
  const order = [];
  for (const i of cells) {
    let edge = false;
    neighbours(g, i, n => { if (!inSet[n]) edge = true; });
    if (edge) { seen[i] = 1; order.push(i); }
  }
  for (let k = 0; k < order.length; k++) {
    neighbours(g, order[k], n => { if (inSet[n] && !seen[n]) { seen[n] = 1; order.push(n); } });
  }
  return order;
}

// Grow the dark `rings` steps. Returns the new cells ring by ring.
export function spreadDark(g, turn, rings) {
  const { order, dist } = flood(g, darkCells(g), turn, rings);
  if (!rings) return [];
  const byRing = Array.from({ length: rings }, () => []);
  for (const i of order) if (dist[i] > 0 && !g.dark[i]) { g.dark[i] = 1; byRing[dist[i] - 1].push(i); }
  return byRing;
}
